<div
  *ngIf="{ latestFwVersions: latestFwVersions$ | async } as resolved"
  class="tw-relative">
  <ng-container *ngTemplateOutlet="templateRef ?? defaultTemplate; context: { latestFwVersions: resolved.latestFwVersions }"></ng-container>
</div>

<ng-template
  #defaultTemplate
  let-latestFwVersions="latestFwVersions">
  <ng-container [ngSwitch]="uiFramework">
    <ng-container *ngSwitchCase="'material'">
      <ng-container *ngTemplateOutlet="updatingLayout"></ng-container>

      <section class="fx-col fx-gap-3">
        <ng-container *ngTemplateOutlet="moduleVersionError"></ng-container>

        <ng-container *ngIf="!isDeviceNotSupported && oneClickGate && oneClickCtx">
          <lib-one-click-panel
            [uiFramework]="uiFramework"
            [labels]="labels"
            [inverterSn]="oneClickCtx.inverterSn"
            [loggerId]="oneClickCtx.loggerId"
            [inverterId]="oneClickCtx.inverterId"
            [solarmanDeviceId]="oneClickCtx.solarmanDeviceId"
            [gate]="oneClickGate"
            [config]="inverterUpgradeConfig"
            [currentVersions]="oneClickCtx.currentVersions"
            (refreshRequested)="refresh$.next()"
            (upgradeInFlight)="onOneClickUpgradeInFlight($event)">
          </lib-one-click-panel>
        </ng-container>

        <ng-container *ngIf="!isDeviceNotSupported && !oneClickGate">
          <ng-container *ngIf="latestFwVersions?.length || latestFwVersions === null; else noUpdatesFirmware">
            <!-- Desktop Table -->
            <azzurro-table
              id="firmware-table"
              [uiFramework]="'material'"
              [columns]="tableColumns"
              [data$]="latestFwVersions$"
              [mode]="'client'"
              [noResultTemplate]="noUpdatesTemplate">
            </azzurro-table>

            <!-- Mobile List -->
            <azzurro-table
              id="firmware-list"
              [uiFramework]="'ionic'"
              [columns]="tableColumns"
              [data$]="latestFwVersions$"
              [mode]="'client'"
              [noResultTemplate]="noUpdatesTemplate">
            </azzurro-table>

            <!-- AH: Batch upgrade button -->
            <div *ngIf="isAzzurroHub && selectedModules.size > 0" class="fx-row fx-justify-end mt-2">
              <button
                mat-raised-button
                color="accent"
                class="batch-upgrade-button"
                [disabled]="isAnyUpgradeInProgress$ | async"
                (click)="sendBatchUpgradeCommand()">
                <mat-icon class="mr-1">system_update</mat-icon>
                {{ labels.upgrade }} ({{ selectedModules.size }})
              </button>
            </div>

            <!-- Safety visible only if device is upgradable and no upgrade in progress -->
            <ng-container *ngIf="(isDeviceUpgradable$ | async) && !(isAnyUpgradeInProgress$ | async)">
              <ng-container *ngIf="isSafetyExpanded; else safetyButtonMaterial">
                <lib-upgrade-safety
                  *ngIf="_inverterId$ | async as deviceUid"
                  [ngClass]="{'bg-accent-100 radius-4 border-1 border-accent-200': uiFramework === 'ionic'}"
                  [uiFramework]="uiFramework"
                  [labels]="labels"
                  [inverterSn]="deviceUid"
                  [system]="system"
                  [disabled]="!(isOnline$ | async)"
                  [channelType]="isAzzurroHub ? ahChannelType : undefined"
                  [gatewaySN]="gatewaySN"
                  [modbusAddress]="_ahModbusAddress">
                </lib-upgrade-safety>
              </ng-container>
            </ng-container>
          </ng-container>
        </ng-container>
      </section>
    </ng-container>

    <ng-container *ngSwitchCase="'ionic'">
      <ng-container *ngTemplateOutlet="updatingLayout"></ng-container>

      <section class="fx-col fx-gap-3">
        <ng-container *ngTemplateOutlet="moduleVersionError"></ng-container>

        <ng-container *ngIf="!isDeviceNotSupported && oneClickGate && oneClickCtx">
          <lib-one-click-panel
            [uiFramework]="uiFramework"
            [labels]="labels"
            [inverterSn]="oneClickCtx.inverterSn"
            [loggerId]="oneClickCtx.loggerId"
            [inverterId]="oneClickCtx.inverterId"
            [solarmanDeviceId]="oneClickCtx.solarmanDeviceId"
            [gate]="oneClickGate"
            [config]="inverterUpgradeConfig"
            [currentVersions]="oneClickCtx.currentVersions"
            (refreshRequested)="refresh$.next()"
            (upgradeInFlight)="onOneClickUpgradeInFlight($event)">
          </lib-one-click-panel>
        </ng-container>

        <!-- Hide table/noUpdates when device is not supported -->
        <ng-container *ngIf="!isDeviceNotSupported && !oneClickGate">
          <ng-container *ngIf="latestFwVersions?.length || latestFwVersions === null; else noUpdatesFirmware">
            <!-- Ionic List -->
            <azzurro-table
              [uiFramework]="'ionic'"
              [columns]="tableColumns"
              [data$]="latestFwVersions$"
              [mode]="'client'"
              [noResultTemplate]="noUpdatesTemplate">
            </azzurro-table>

            <!-- Safety visible only if device is upgradable and no upgrade in progress -->
            <ng-container *ngIf="(isDeviceUpgradable$ | async) && !(isAnyUpgradeInProgress$ | async)">
              <ng-container *ngIf="isSafetyExpanded; else safetyButtonIonic">
                <lib-upgrade-safety
                  *ngIf="_inverterId$ | async as deviceUid"
                  [ngClass]="{'bg-accent-100 radius-4 border-1 border-accent-200': uiFramework === 'ionic'}"
                  [uiFramework]="uiFramework"
                  [labels]="labels"
                  [inverterSn]="deviceUid"
                  [system]="system"
                  [disabled]="!(isOnline$ | async)"
                  [channelType]="isAzzurroHub ? ahChannelType : undefined"
                  [gatewaySN]="gatewaySN"
                  [modbusAddress]="_ahModbusAddress">
                </lib-upgrade-safety>
              </ng-container>
            </ng-container>
          </ng-container>
        </ng-container>
      </section>
    </ng-container>
  </ng-container>
</ng-template>

<ng-template #updatingLayout>
  <!-- Overlay spinner only for Solarman — AH shows inline progress per module -->
  <div
    *ngIf="isUpdating && !isAzzurroHub && !oneClickGate"
    class="centered-overlay fx-col fx-center h-50 p-4 radius-4">
    <mat-progress-spinner
      class="fx-g-1"
      mode="indeterminate"
      diameter="50">
    </mat-progress-spinner>
    <div class="text-grey-400 tw-font-medium">{{ labels.updatingMessage }}</div>
  </div>
</ng-template>

<ng-template #moduleVersionError>
  <!-- Inconsistent firmware warning -->
  <section
    *ngIf="hasInconsistentFw$ | async"
    class="border-1 fx-row fx-gap-3 p-1 radius-4 radius-2 border-grey-100 bg-yellow-100 px-2">
    <ion-icon
      class="text-orange-400 min-s-6 s-6 pt-4"
      name="information-circle-outline">
    </ion-icon>
    <customer-title-content
      class="fx-g-1"
      [content]="labels.inconsistentFw">
    </customer-title-content>
  </section>

  <!-- Device not supported warning -->
  <section
    *ngIf="isDeviceNotSupported"
    class="border-1 fx-row fx-gap-3 p-1 radius-4 radius-2 border-grey-100 bg-red-100 px-2">
    <ion-icon
      class="text-red-400 min-s-6 s-6"
      name="close-circle-outline">
    </ion-icon>
    <customer-title-content
      class="fx-g-1"
      [content]="labels.deviceNotSupported">
    </customer-title-content>
  </section>

  <!-- Rate limited warning -->
  <section
    *ngIf="isRateLimited"
    class="border-1 fx-row fx-gap-3 p-1 radius-4 radius-2 border-grey-100 bg-yellow-100 px-2">
    <ion-icon
      class="text-orange-400 min-s-6 s-6"
      name="time-outline">
    </ion-icon>
    <customer-title-content
      class="fx-g-1"
      [content]="labels.rateLimited">
    </customer-title-content>
  </section>
</ng-template>

<ng-template
  #noUpdatesFirmware
  let-latestFwVersions="latestFwVersions">
  <div
    *ngIf="latestFwVersions !== null"
    class="fx-col fx-gap-3">
    <div class="p-3 text-center text-grey-400 w-full bg-green-100 border-1 radius-4 border-green-100">
      {{ labels.noUpdates }}
    </div>
    <lib-device-details
      *ngIf="_inverterId$ | async as deviceUid"
      customerScreenType
      [ngClass]="{'bg-accent-100 radius-4 border-1 border-accent-200': uiFramework === 'ionic'}"
      [uiFramework]="uiFramework"
      [refresh$]="refresh$"
      [labels]="labels"
      [deviceUid]="deviceUid"
      [system]="system">
    </lib-device-details>
    <!-- Safety visible only if device is upgradable and no upgrade in progress -->
    <ng-container *ngIf="(isDeviceUpgradable$ | async) && !(isAnyUpgradeInProgress$ | async)">
      <ng-container *ngIf="isSafetyExpanded; else safetyButtonNoUpdates">
        <lib-upgrade-safety
          *ngIf="_inverterId$ | async as deviceUid"
          [ngClass]="{'bg-accent-100 radius-4 border-1 border-accent-200': uiFramework === 'ionic'}"
          [uiFramework]="uiFramework"
          [labels]="labels"
          [inverterSn]="deviceUid"
          [system]="system"
          [disabled]="!(isOnline$ | async)"
          [channelType]="isAzzurroHub ? ahChannelType : undefined"
          [gatewaySN]="gatewaySN"
          [modbusAddress]="_ahModbusAddress">
        </lib-upgrade-safety>
      </ng-container>
    </ng-container>
  </div>
</ng-template>

<!-- Templates for azzurro-table -->

<!-- No updates template -->
<ng-template #noUpdatesTemplate>
  <div class="p-3 text-center text-grey-400 w-full bg-green-100 border-1 radius-4 border-green-100">
    {{ labels.noUpdates }}
  </div>
</ng-template>

<!-- Current Version template -->
<ng-template #currentVersionTemplate let-row>
  <span class="version-badge version-current">
    {{ row.currentVersion }}
  </span>
</ng-template>

<!-- Latest Version template -->
<ng-template #latestVersionTemplate let-row>
  <span class="version-badge version-latest">
    {{ row.latestVersion }}
  </span>
</ng-template>

<!-- AH: Select + progress template -->
<ng-template #selectTemplate let-row>
  <ng-container *ngIf="{ progress: moduleProgress$ | async } as ctx">
    <!-- Show status badge if module is upgrading -->
    <ng-container *ngIf="ctx.progress?.modules?.[row?.module] as modProgress">
      <ng-container *ngIf="modProgress.status != null && modProgress.status !== 7">
        <div class="upgrading-indicator fx-row fx-center fx-gap-2">
          <mat-icon class="text-accent spin s-5">sync</mat-icon>
          <span class="text-accent text-sm tw-font-medium tw-whitespace-nowrap">
            {{ getUpgradeStatusLabel(modProgress.status, modProgress.progress) }}
          </span>
        </div>
      </ng-container>
    </ng-container>

    <!-- Checkbox when not upgrading -->
    <div
      *ngIf="!ctx.progress?.modules?.[row?.module] || ctx.progress?.modules?.[row?.module]?.status == null || ctx.progress?.modules?.[row?.module]?.status === 7"
      class="fx-row fx-justify-end">
      <mat-checkbox
        class="custom-checkbox"
        [checked]="isModuleSelected(row?.module)"
        [disabled]="ctx.progress?.updating"
        (change)="toggleModuleSelection(row?.module)"
        color="accent">
      </mat-checkbox>
    </div>
  </ng-container>
</ng-template>

<!-- Solarman: Actions template -->
<ng-template #actionsTemplate let-row>
  <ng-container *ngIf="{ fwList: latestFwVersions$ | async, progress: moduleProgress$ | async } as resolved">
    <!-- AH: show status badge when module is upgrading -->
    <ng-container *ngIf="isAzzurroHub && resolved.progress?.modules?.[row?.module] as modProgress">
      <ng-container *ngIf="modProgress.status !== 7 && modProgress.status !== null">
        <div class="upgrading-indicator fx-row fx-center fx-gap-2">
          <mat-icon class="text-accent spin s-5">sync</mat-icon>
          <span class="text-accent text-sm tw-font-medium tw-whitespace-nowrap">
            {{ getUpgradeStatusLabel(modProgress.status, modProgress.progress) }}
          </span>
        </div>
      </ng-container>
    </ng-container>

    <!-- Default: upgrade button (Solarman or AH when no upgrade in progress for this module) -->
    <ng-container *ngIf="!isAzzurroHub || !resolved.progress?.modules?.[row?.module] || resolved.progress?.modules?.[row?.module]?.status === 7 || resolved.progress?.modules?.[row?.module]?.status == null">
      <button
        type="button"
        mat-icon-button
        class="action-button ml-auto"
        [matTooltip]="labels.upgrade"
        [disabled]="isAzzurroHub
          ? (resolved.progress?.modules?.[row?.module]?.status != null && resolved.progress?.modules?.[row?.module]?.status !== 7)
          : (isUpdating || row?.isUpgrading || (resolved.fwList?.[0]?.module === 'LOGGER' && row?.module !== 'LOGGER'))"
        (click)="sendUpgradeCommand(row)">
        <mat-icon
          [class.text-grey-300]="isAzzurroHub
            ? (resolved.progress?.modules?.[row?.module]?.status != null && resolved.progress?.modules?.[row?.module]?.status !== 7)
            : (isUpdating || row?.isUpgrading || (resolved.fwList?.[0]?.module === 'LOGGER' && row?.module !== 'LOGGER'))"
          [class.text-accent]="isAzzurroHub
            ? !(resolved.progress?.modules?.[row?.module]?.status != null && resolved.progress?.modules?.[row?.module]?.status !== 7)
            : !(isUpdating || row?.isUpgrading || (resolved.fwList?.[0]?.module === 'LOGGER' && row?.module !== 'LOGGER'))">
          system_update
        </mat-icon>
      </button>
    </ng-container>
  </ng-container>
</ng-template>

<!-- Safety lazy-load button: Material -->
<ng-template #safetyButtonMaterial>
  <button
    mat-button
    class="safety-expand-button fx-row fx-center fx-gap-2 w-full p-3 border-1 radius-4 border-grey-100 bg-grey-50"
    [disabled]="!(isOnline$ | async)"
    (click)="expandSafety()">
    <mat-icon class="text-accent">security</mat-icon>
    <span class="text-grey-400 tw-font-medium">{{ labels.loadSafetyButton }}</span>
  </button>
</ng-template>

<!-- Safety lazy-load button: Ionic -->
<ng-template #safetyButtonIonic>
  <button
    mat-button
    class="safety-expand-button fx-row fx-center fx-gap-2 w-full p-3 radius-4 bg-accent-100 border-1 border-accent-200"
    [disabled]="!(isOnline$ | async)"
    (click)="expandSafety()">
    <mat-icon class="text-accent">security</mat-icon>
    <span class="text-grey-400 tw-font-medium">{{ labels.loadSafetyButton }}</span>
  </button>
</ng-template>

<!-- Safety lazy-load button: No-updates (framework-aware) -->
<ng-template #safetyButtonNoUpdates>
  <ng-container *ngTemplateOutlet="uiFramework === 'material' ? safetyButtonMaterial : safetyButtonIonic">
  </ng-container>
</ng-template>
